/**
 * Acknowledging a station alert (step 7b): one tap on the floor. The presets say what was done; words are needed only
 * for "other", and may be added to any. A change or a call to maintenance goes on the Actions list as a change tried,
 * so its effect is followed. Pure.
 */
import { Loader2 } from "lucide-react";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { cn } from "@/lib/ui/cn";
import type { StationAlertAckKind } from "@/lib/api/station-alerts";
import { ACK_DETAIL_MAX, ACK_PRESETS, ackProblem, ackTried } from "@/lib/plant/station-alerts";

export interface AckFormProps {
  kind: StationAlertAckKind | "";
  detail: string;
  saving: boolean;
  error: string | null;
  /** The id of the alert's headline, which names the form for a screen reader. */
  describedBy?: string;
  onKind: (k: StationAlertAckKind) => void;
  onDetail: (d: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

export function AckForm(p: AckFormProps) {
  const problem = ackProblem(p.kind, p.detail);
  return (
    <form
      aria-label="Acknowledge the alert"
      aria-describedby={p.describedBy}
      className="flex flex-col gap-2 rounded-md border border-border p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!problem && !p.saving) p.onSave();
      }}
    >
      <div role="radiogroup" aria-label="What was done" className="flex flex-wrap gap-1.5">
        {ACK_PRESETS.map((x) => (
          <button
            key={x.kind}
            type="button"
            role="radio"
            aria-checked={p.kind === x.kind}
            onClick={() => p.onKind(x.kind)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
              p.kind === x.kind
                ? "border-brand bg-brand text-brand-foreground"
                : "border-border bg-surface text-on-surface hover:bg-surface-muted",
            )}
          >
            {x.label}
          </button>
        ))}
      </div>
      <Input
        aria-label="What was done, in words"
        placeholder={p.kind === "other" ? "What was done" : "Detail (optional)"}
        value={p.detail}
        maxLength={ACK_DETAIL_MAX}
        onChange={(e) => p.onDetail(e.target.value)}
        className="h-9"
      />
      {p.kind && ackTried(p.kind) && (
        <p className="text-xs text-on-surface-muted">It goes on the Actions list as a change tried, so its effect is followed.</p>
      )}
      <div className="flex items-center gap-2">
        <p className="min-h-4 flex-1 text-xs text-on-surface-muted">{p.kind ? (problem ?? "") : ""}</p>
        <Button type="button" size="sm" variant="ghost" onClick={p.onCancel} disabled={p.saving}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={problem !== null || p.saving}>
          {p.saving && <Loader2 className="animate-spin" />} Acknowledge
        </Button>
      </div>
      {p.error && (
        <p role="alert" className="text-sm text-danger">
          {p.error}
        </p>
      )}
    </form>
  );
}
